/**
 * Team Lobby Component
 * Shows lobby where teams select their mascots and wait for quiz to start
 */

import React, { useCallback, useMemo, useState, useRef, memo, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  Dimensions,
  RefreshControl,
  FlatList,
  ViewToken,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useQuizLive, MascotSelection, LiveQuizParticipant } from "../index";
import { LOTTIE_CATEGORY_LABELS, LottieCategory } from "@/lib/lottie-registry";

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const ITEMS_PER_ROW = 4;
const MASCOT_SIZE = (SCREEN_WIDTH - 64) / ITEMS_PER_ROW;

/**
 * Row item for FlatList - contains category header or mascot row
 */
type MascotPickerRowItem = 
  | { type: "header"; category: string }
  | { type: "row"; items: MascotSelection[] };

/**
 * Memoized Mascot item component with lazy animation
 * Only plays animation when visible in viewport
 */
const MascotPickerItem = memo(({ 
  mascot, 
  isSelected, 
  isTaken,
  isVisible,
  onSelect, 
  quizColor,
  colors 
}: { 
  mascot: MascotSelection; 
  isSelected: boolean; 
  isTaken: boolean;
  isVisible: boolean;
  onSelect: (key: string) => void;
  quizColor: string;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  return (
    <TouchableOpacity
      onPress={() => onSelect(mascot.mascot_key)}
      disabled={isTaken && !isSelected}
      className="mb-3 items-center"
      style={{
        width: MASCOT_SIZE,
        opacity: isTaken && !isSelected ? 0.4 : 1,
      }}
      accessibilityLabel={`Selecteer ${mascot.mascot.name}`}
      accessibilityRole="button"
    >
      <View
        className="rounded-xl overflow-hidden items-center justify-center"
        style={{
          width: MASCOT_SIZE - 8,
          height: MASCOT_SIZE - 8,
          backgroundColor: isSelected ? `${quizColor}20` : colors.surface,
          borderWidth: isSelected ? 3 : 1,
          borderColor: isSelected ? quizColor : colors.border,
        }}
      >
        {isVisible ? (
          <LottieView
            source={mascot.mascot.source}
            autoPlay
            loop
            style={{ width: MASCOT_SIZE - 10, height: MASCOT_SIZE - 10 }}
          />
        ) : (
          <View 
            style={{ 
              width: MASCOT_SIZE - 10, 
              height: MASCOT_SIZE - 10,
              backgroundColor: colors.surfaceSecondary,
              borderRadius: 8,
            }} 
          />
        )}
        {isTaken && !isSelected && (
          <View className="absolute inset-0 bg-black/50 items-center justify-center">
            <Ionicons name="lock-closed" size={20} color="#FFFFFF" />
          </View>
        )}
        {isSelected && (
          <View 
            className="absolute top-1 right-1 w-5 h-5 rounded-full items-center justify-center" 
            style={{ backgroundColor: quizColor }}
          >
            <Ionicons name="checkmark" size={14} color="#FFFFFF" />
          </View>
        )}
      </View>
      <Text
        className="text-xs text-center mt-1"
        style={{ color: colors.textSecondary }}
        numberOfLines={1}
      >
        {mascot.mascot.name}
      </Text>
    </TouchableOpacity>
  );
});

/**
 * Optimized Mascot Picker Modal with FlatList virtualization
 * Only animates visible items for better performance
 */
const MascotPickerModal = memo(({
  visible,
  selectedMascotKey,
  mascotsByCategory,
  onSelect,
  onClose,
  quizColor,
  colors,
}: {
  visible: boolean;
  selectedMascotKey: string | null;
  mascotsByCategory: Record<string, MascotSelection[]>;
  onSelect: (key: string) => void;
  onClose: () => void;
  quizColor: string;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  // Track which mascot keys are currently visible in viewport
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  
  // Build flat list data with headers and rows
  const flatListData = useMemo((): MascotPickerRowItem[] => {
    const data: MascotPickerRowItem[] = [];
    
    for (const [category, mascots] of Object.entries(mascotsByCategory)) {
      if (!mascots || mascots.length === 0) continue;
      
      // Add category header
      data.push({ type: "header", category });
      
      // Group items into rows of ITEMS_PER_ROW
      const validMascots = mascots.filter(m => m.mascot?.source != null);
      for (let i = 0; i < validMascots.length; i += ITEMS_PER_ROW) {
        data.push({ 
          type: "row", 
          items: validMascots.slice(i, i + ITEMS_PER_ROW) 
        });
      }
    }
    
    return data;
  }, [mascotsByCategory]);
  
  // Handle viewable items change - update which mascots should animate
  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    const newVisibleKeys = new Set<string>();
    
    for (const viewable of viewableItems) {
      const item = viewable.item as MascotPickerRowItem;
      if (item.type === "row") {
        for (const mascot of item.items) {
          newVisibleKeys.add(mascot.mascot_key);
        }
      }
    }
    
    setVisibleKeys(newVisibleKeys);
  }).current;
  
  // Viewability config - items are considered visible when 10% is in view
  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 10,
    minimumViewTime: 100,
  }).current;
  
  // Get unique key for each row
  const getItemKey = useCallback((item: MascotPickerRowItem, index: number): string => {
    if (item.type === "header") {
      return `header-${item.category}`;
    }
    return `row-${index}-${item.items.map(m => m.mascot_key).join("-")}`;
  }, []);
  
  // Render a single row (header or mascot row)
  const renderItem = useCallback(({ item }: { item: MascotPickerRowItem }) => {
    if (item.type === "header") {
      return (
        <View className="mb-3 mt-2">
          <Text 
            className="text-lg font-semibold" 
            style={{ color: colors.text }}
          >
            {LOTTIE_CATEGORY_LABELS[item.category as LottieCategory] || item.category}
          </Text>
        </View>
      );
    }
    
    return (
      <View className="flex-row">
        {item.items.map((mascot) => (
          <MascotPickerItem
            key={mascot.mascot_key}
            mascot={mascot}
            isSelected={selectedMascotKey === mascot.mascot_key}
            isTaken={mascot.selected_by !== null && selectedMascotKey !== mascot.mascot_key}
            isVisible={visibleKeys.has(mascot.mascot_key)}
            onSelect={onSelect}
            quizColor={quizColor}
            colors={colors}
          />
        ))}
      </View>
    );
  }, [selectedMascotKey, visibleKeys, onSelect, quizColor, colors]);
  
  // Reset visible keys when modal closes
  useEffect(() => {
    if (!visible) {
      setVisibleKeys(new Set());
    }
  }, [visible]);
  
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView 
        className="flex-1" 
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        {/* Modal Header */}
        <View
          className="px-4 py-4 flex-row items-center justify-between border-b"
          style={{ borderBottomColor: colors.border }}
        >
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            Kies een Mascotte
          </Text>
          <TouchableOpacity 
            onPress={onClose}
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={28} color={colors.text} />
          </TouchableOpacity>
        </View>
        
        {/* Virtualized Mascot Grid */}
        <FlatList
          data={flatListData}
          renderItem={renderItem}
          keyExtractor={getItemKey}
          contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          removeClippedSubviews={true}
          maxToRenderPerBatch={5}
          windowSize={5}
          initialNumToRender={8}
          getItemLayout={(data, index) => ({
            length: data?.[index]?.type === "header" ? 40 : MASCOT_SIZE + 12,
            offset: index * (MASCOT_SIZE + 12),
            index,
          })}
        />
      </SafeAreaView>
    </Modal>
  );
});

interface TeamLobbyProps {
  sessionId: string;
  onLeave: () => void;
}

/**
 * Team Lobby - Mascot selection and ready state
 */
export const TeamLobby: React.FC<TeamLobbyProps> = ({ sessionId, onLeave }) => {
  const colors = useThemeColors();
  const [showMascotPicker, setShowMascotPicker] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const {
    session,
    quiz,
    participants,
    myParticipant,
    availableMascots,
    selectedMascot,
    isLoading,
    isConnected,
    error,
    selectMascot,
    setReady,
    leaveSession,
    refreshSession,
  } = useQuizLive({ sessionId });
  
  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refreshSession();
    setIsRefreshing(false);
  }, [refreshSession]);
  
  // Group mascots by category for picker
  const mascotsByCategory = useMemo(() => {
    const grouped: Record<string, MascotSelection[]> = {};
    for (const mascot of availableMascots) {
      const category = mascot.mascot.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(mascot);
    }
    return grouped;
  }, [availableMascots]);
  
  /**
   * Handle mascot selection
   */
  const handleSelectMascot = useCallback(async (mascotKey: string) => {
    const success = await selectMascot(mascotKey);
    if (success) {
      setShowMascotPicker(false);
    }
  }, [selectMascot]);
  
  /**
   * Handle ready button
   */
  const handleReady = useCallback(async () => {
    if (!selectedMascot) {
      Alert.alert("Kies een mascotte", "Je moet eerst een mascotte kiezen voordat je klaar bent.");
      return;
    }
    await setReady();
  }, [selectedMascot, setReady]);
  
  /**
   * Handle leave
   */
  const handleLeave = useCallback(async () => {
    Alert.alert(
      "Quiz verlaten?",
      "Weet je zeker dat je de quiz wilt verlaten?",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verlaten",
          style: "destructive",
          onPress: async () => {
            await leaveSession();
            onLeave();
          },
        },
      ]
    );
  }, [leaveSession, onLeave]);
  
  if (!session || !quiz) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </View>
    );
  }
  
  const isReady = myParticipant?.is_ready || false;
  const readyCount = participants.filter(p => p.is_ready).length;
  const totalCount = participants.length;
  
  return (
    <View className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Compact Header */}
      <LinearGradient
        colors={[quiz.color || "#8B5CF6", "#4F46E5"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        className="px-4 py-3 pt-10"
      >
        <View className="flex-row items-center justify-between">
          <TouchableOpacity onPress={handleLeave} className="p-1">
            <Ionicons name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View className="flex-1 flex-row items-center justify-center mx-3">
            <View className="w-8 h-8 rounded-lg bg-white/20 items-center justify-center mr-2">
              <Ionicons name={quiz.icon as any} size={18} color="#FFFFFF" />
            </View>
            <Text className="text-white text-base font-bold" numberOfLines={1}>
              {quiz.name}
            </Text>
          </View>
          
          <View className="flex-row items-center px-2 py-1 rounded-full bg-white/20">
            <View className={`w-2 h-2 rounded-full mr-1.5 ${isConnected ? "bg-green-400" : "bg-orange-400"}`} />
            <Text className="text-white text-xs">
              {isConnected ? "Verbonden" : "Verbinden..."}
            </Text>
          </View>
        </View>
      </LinearGradient>
      
      {/* Error */}
      {error && (
        <View className="mx-4 mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
          <Text className="text-red-500">{error}</Text>
        </View>
      )}
      
      {/* My Mascot Selection */}
      <View className="mx-4 mt-6">
        <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
          Jouw Team Mascotte
        </Text>
        
        <TouchableOpacity
          onPress={() => setShowMascotPicker(true)}
          disabled={isReady}
          className="rounded-2xl p-4 flex-row items-center"
          style={{
            backgroundColor: selectedMascot ? `${quiz.color}15` : colors.surface,
            borderWidth: 2,
            borderColor: selectedMascot ? quiz.color : colors.border,
            opacity: isReady ? 0.6 : 1,
          }}
        >
          {selectedMascot ? (
            <>
              <View className="w-20 h-20 rounded-xl overflow-hidden bg-white items-center justify-center">
                {selectedMascot.mascot?.source ? (
                  <LottieView
                    source={selectedMascot.mascot.source}
                    autoPlay
                    loop
                    style={{ width: 80, height: 80 }}
                  />
                ) : (
                  <Ionicons name="paw" size={40} color={colors.textTertiary} />
                )}
              </View>
              <View className="flex-1 ml-4">
                <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                  {selectedMascot.mascot.name}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  Tik om te wijzigen
                </Text>
              </View>
              {!isReady && (
                <Ionicons name="chevron-forward" size={24} color={colors.textTertiary} />
              )}
            </>
          ) : (
            <>
              <View
                className="w-20 h-20 rounded-xl items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="add" size={32} color={colors.textTertiary} />
              </View>
              <View className="flex-1 ml-4">
                <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                  Kies een mascotte
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  Selecteer jouw team mascotte
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={24} color={colors.textTertiary} />
            </>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Teams Waiting */}
      <View className="mx-4 mt-6 flex-1">
        <View className="flex-row items-center justify-between mb-3">
          <Text className="text-lg font-semibold" style={{ color: colors.text }}>
            Teams ({readyCount}/{totalCount} klaar)
          </Text>
          {readyCount === totalCount && totalCount > 0 && (
            <View className="px-2 py-1 rounded-full bg-green-500/20">
              <Text className="text-green-600 text-xs font-medium">Alle teams klaar!</Text>
            </View>
          )}
        </View>
        
        <ScrollView 
          className="flex-1" 
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor={quiz?.color || colors.primary}
              colors={[quiz?.color || colors.primary]}
            />
          }
        >
          {participants.length === 0 ? (
            <View
              className="p-6 rounded-xl items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Ionicons name="hourglass-outline" size={48} color={colors.textTertiary} />
              <Text className="mt-3" style={{ color: colors.textSecondary }}>
                Wachten op teams...
              </Text>
            </View>
          ) : (
            <View className="gap-2">
              {participants.map((participant) => (
                <TeamParticipantCard
                  key={participant.id}
                  participant={participant}
                  isMe={participant.id === myParticipant?.id}
                  quizColor={quiz.color}
                />
              ))}
            </View>
          )}
          <View className="h-4" />
        </ScrollView>
      </View>
      
      {/* Ready Button */}
      <View className="px-4 pb-8 pt-4" style={{ backgroundColor: colors.background }}>
        <TouchableOpacity
          onPress={handleReady}
          disabled={!selectedMascot || isReady || isLoading}
          className="rounded-2xl p-4 flex-row items-center justify-center"
          style={{
            backgroundColor: isReady ? "#10B981" : !selectedMascot ? colors.surfaceSecondary : quiz.color,
            opacity: !selectedMascot && !isReady ? 0.5 : 1,
          }}
        >
          {isLoading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : isReady ? (
            <>
              <Ionicons name="time-outline" size={24} color="#FFFFFF" />
              <Text className="text-white text-lg font-bold ml-2">
                Wachten op andere teams...
              </Text>
            </>
          ) : (
            <>
              <Ionicons name="rocket" size={24} color="#FFFFFF" />
              <Text className="text-white text-lg font-bold ml-2">
                Klaar om te spelen
              </Text>
            </>
          )}
        </TouchableOpacity>
        
        {isReady && (
          <Text className="text-center mt-3" style={{ color: colors.textSecondary }}>
            Wachten tot de quizmaster de quiz start...
          </Text>
        )}
      </View>
      
      {/* Optimized Mascot Picker Modal */}
      <MascotPickerModal
        visible={showMascotPicker}
        selectedMascotKey={selectedMascot?.mascot_key || null}
        mascotsByCategory={mascotsByCategory}
        onSelect={handleSelectMascot}
        onClose={() => setShowMascotPicker(false)}
        quizColor={quiz.color}
        colors={colors}
      />
    </View>
  );
};

/**
 * Team Participant Card
 */
interface TeamParticipantCardProps {
  participant: LiveQuizParticipant;
  isMe: boolean;
  quizColor: string;
}

const TeamParticipantCard: React.FC<TeamParticipantCardProps> = ({
  participant,
  isMe,
  quizColor,
}) => {
  const colors = useThemeColors();
  const mascotLottie = availableMascotsGlobal.find(m => m.mascot_key === participant.mascot_key);
  
  return (
    <View
      className="rounded-xl p-3 flex-row items-center"
      style={{
        backgroundColor: isMe ? `${quizColor}10` : colors.surface,
        borderWidth: isMe ? 2 : 1,
        borderColor: isMe ? quizColor : colors.border,
      }}
    >
      {/* Mascot */}
      <View
        className="w-12 h-12 rounded-lg overflow-hidden items-center justify-center"
        style={{ backgroundColor: colors.surfaceSecondary }}
      >
        {mascotLottie?.mascot?.source ? (
          <LottieView
            source={mascotLottie.mascot.source}
            autoPlay
            loop
            style={{ width: 48, height: 48 }}
          />
        ) : (
          <Ionicons name="person" size={24} color={colors.textTertiary} />
        )}
      </View>
      
      {/* Info */}
      <View className="flex-1 ml-3">
        <View className="flex-row items-center">
          <Text className="font-semibold" style={{ color: colors.text }}>
            {participant.team?.name || "Team"}
          </Text>
          {isMe && (
            <View className="ml-2 px-2 py-0.5 rounded-full" style={{ backgroundColor: quizColor }}>
              <Text className="text-white text-xs font-medium">Jij</Text>
            </View>
          )}
        </View>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {participant.player?.full_name || participant.player?.nickname || "Speler"}
        </Text>
      </View>
      
      {/* Ready Status */}
      <View
        className="px-3 py-1.5 rounded-full"
        style={{
          backgroundColor: participant.is_ready ? "#10B98120" : colors.surfaceSecondary,
        }}
      >
        {participant.is_ready ? (
          <View className="flex-row items-center">
            <Ionicons name="checkmark-circle" size={16} color="#10B981" />
            <Text className="ml-1 text-sm font-medium" style={{ color: "#10B981" }}>
              Klaar
            </Text>
          </View>
        ) : (
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            Wacht...
          </Text>
        )}
      </View>
    </View>
  );
};

// Need to access LOTTIE_REGISTRY for the card
import { LOTTIE_REGISTRY } from "@/lib/lottie-registry";

const availableMascotsGlobal: MascotSelection[] = LOTTIE_REGISTRY
  .filter(l => ["animal", "monster", "fruit", "emoji"].includes(l.category) && l.source != null)
  .map(mascot => ({
    mascot_key: mascot.key,
    mascot,
    selected_by: null,
  }));

